import { Injectable, inject, type Type } from '@angular/core';
import {
  catchError,
  from,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  ConfirmationDrawerService,
  type ConfirmationDrawerConfig,
} from '@ps/core-overlay';
import { SURVEY_STATUS, SurveyStatus } from '@ps/survey-kernel';
import { Nullable } from '@ps/utils';
import {
  HIGH_CPI_CONFIRMATION_TITLES,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyHighCpiConfirmationInspector,
} from '@ps/survey-utils';

import { SurveyService } from '../survey/survey.service';
import { SurveyActivityLogService } from '../survey-activity-log/survey-activity-log.service';

export interface SurveyHighCpiConfirmationOptions {
  readonly isProject?: boolean;
  readonly nextStatus?: SurveyStatus;
}

export interface SurveyHighCpiEnteredConfirmationOptions {
  readonly currencySymbol?: string;
  readonly title?: string;
  readonly body?: Type<unknown>;
  readonly bodyInputs?: Record<string, unknown>;
  readonly drawerConfig?: ConfirmationDrawerConfig;
}

@Injectable()
export class SurveyHighCpiConfirmationService {
  private readonly _confirmationDrawer = inject(ConfirmationDrawerService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _activityLogService = inject(SurveyActivityLogService);

  /**
   * Confirms high CPI using the survey high-cpi-details API (status change to Live).
   */
  public request(
    surveyId: number,
    options: SurveyHighCpiConfirmationOptions = {},
  ): Observable<boolean> {
    const nextStatus = options.nextStatus ?? SURVEY_STATUS.LIVE;

    if (nextStatus !== SURVEY_STATUS.LIVE) {
      return of(true);
    }

    return this._surveyService.getHighCpiDetails(surveyId).pipe(
      switchMap((highCpiDetails) =>
        this._confirmIfNeeded(
          new SurveyHighCpiConfirmationInspector({
            type: 'high-cpi-data',
            nextStatus,
            highCpiDetails,
            isProject: options.isProject,
          }),
          {
            surveyIds: [surveyId],
            cpi: highCpiDetails.pricingDetails.value,
          },
          { stacked: true },
        ),
      ),
      catchError(() => of(true)),
    );
  }

  /**
   * Confirms high CPI when the user enters a value greater than {@link MAX_CPI_THRESHOLD}.
   *
   * `surveyId` is nullable for Create Survey, where the draft may not exist yet
   * when the operator confirms: with no id there is nothing to attach an
   * activity log to, so the shared logging step is simply given no surveys and
   * the caller records the confirmation itself once an id arrives.
   */
  public requestForEnteredCpi(
    surveyId: Nullable<number>,
    cpi: number | string,
    options: SurveyHighCpiEnteredConfirmationOptions = {},
  ): Observable<boolean> {
    return this._confirmIfNeeded(
      new SurveyHighCpiConfirmationInspector({
        type: 'survey-data',
        cpi: Number(cpi),
        currencySymbol: options.currencySymbol ?? '$',
      }),
      { surveyIds: surveyId ? [surveyId] : [], cpi },
      { stacked: true, ...options.drawerConfig },
      {
        title: options.title,
        body: options.body,
        bodyInputs: options.bodyInputs,
      },
    );
  }

  /**
   * Confirms high CPI for a bulk CPI edit. Opens the drawer once and logs
   * the confirmation for every survey in the selection.
   */
  public requestForEnteredBulkCpi(
    surveyIds: number[],
    cpi: number | string,
    options: SurveyHighCpiEnteredConfirmationOptions = {},
  ): Observable<boolean> {
    return this._confirmIfNeeded(
      new SurveyHighCpiConfirmationInspector({
        type: 'survey-data',
        cpi: Number(cpi),
        currencySymbol: options.currencySymbol ?? '$',
      }),
      { surveyIds, cpi },
      { ...options.drawerConfig },
    );
  }

  private _confirmIfNeeded(
    inspector: SurveyHighCpiConfirmationInspector,
    activity: { surveyIds: number[]; cpi: number | string },
    drawerOptions: ConfirmationDrawerConfig = {},
    content: {
      title?: string;
      body?: Type<unknown>;
      bodyInputs?: Record<string, unknown>;
    } = {},
  ): Observable<boolean> {
    if (!inspector.check()) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const ref = this._confirmationDrawer.openRef(
        {
          title: content.title ?? HIGH_CPI_CONFIRMATION_TITLES.survey,
          subtitle: content.body
            ? undefined
            : 'Please review the value before confirming',
          message: inspector.text(),
          confirmLabel: 'Confirm',
          cancelLabel: 'Cancel',
          body: content.body,
          bodyInputs: content.body
            ? { message: inspector.text(), ...content.bodyInputs }
            : undefined,
        },
        drawerOptions,
      );

      const sub = ref.closed$
        .pipe(
          take(1),
          tap((confirmed) => {
            if (confirmed === 'confirm') {
              this._logConfirmations(
                activity.surveyIds,
                activity.cpi,
              ).subscribe();
            }
          }),
        )
        .subscribe((confirmed) => {
          if (confirmed === 'confirm') {
            observer.next(true);
          }
          observer.complete();
        });

      // Close the drawer if the parent flow is cancelled (e.g. status drawer
      // backdrop click) so the CPI drawer doesn't stay orphaned on screen.
      return () => {
        sub.unsubscribe();
        ref.close();
      };
    });
  }

  private _logConfirmations(
    surveyIds: number[],
    cpi: number | string,
  ): Observable<void> {
    return from(surveyIds).pipe(
      mergeMap((id) =>
        this._activityLogService.logActivity({
          message: SURVEY_ACTIVITY_LOG_MESSAGES.confirmedHighCpi,
          surveyId: id,
          metadata: { cpi },
        }),
      ),
    );
  }
}
